import React, { Fragment } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import FileSaver from 'file-saver';
import { blankLink } from 'config';
import { useAppLogger } from 'Services/logger';
import { Button, Col, Form, FormControl, FormGroup, Row } from 'react-bootstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { LoadingSpinner, useLoadingSpinnerOnFullContainer } from 'App/components/utils/LoadingSpinner';
import { IProductDataGM, useGarantieMaxActions } from '../../GarantieMax';
import { IBFInsuredObjectItem, IDeviceItem, ITariffDataSend, ITariffDataStep } from 'Services/widgets/interfaces';
import { useAppAlertService } from 'App/components/utils/alerts/AppAlertService';
import { useWidgetService } from 'Services/widget';
import {
  getFactorItem,
  getInsuredObject,
  prepareFormDataGM,
  selectInsuredObject,
  maxCountDevices,
  maxCountMobile,
  updatedAddOnFamilyItems,
  clearAddOnFamilyFields,
} from '../../garantie-max-hooks';
import { DeviceGroups, ActionDeviceTypes, DeviceTypes, FieldDeviceTypes, FamilyTypes } from 'Services/widgets/enums';
import { ICFFactorTypeOption } from '../../../factor-service';
import { SelectDevicePanel } from '../../tariff-elements/SelectDevicePanel';
import { AddDeviceElement } from '../../tariff-elements/AddDeviceElement';
import { FieldPanal } from '../../tariff-elements/FieldPanal';
import { InsuredObjectTypes } from 'Services/widgets/enums';
import { TariffFormReducer, tariffFormReducer } from '../../reducers/tariff-form-reducer';
import { AddOnFamilyPanel } from '../../tariff-elements/AddOnFamilyPanel';

import deviceIconUrl from 'assets/bf-images/device_icon.svg';
import { GMBFStep } from 'App/components/widgets/garantie-max/enum';

export interface TariffSubStep1Props {
  formData: ITariffDataStep | null;
  storeDevices: IDeviceItem[] | null;
  productData: IProductDataGM;
}

export interface TariffFormData {
  tariffFormData: ITariffDataStep | null;
  deviceItems: IDeviceItem[];
}

export interface TariffFormState extends TariffFormData {
  isLoading: boolean;
}

const emptyDevice: IDeviceItem = {
  insuredId: null,
  addedNewDevice: ActionDeviceTypes.No,
  addOnFamily: false,
  showFields: false,
};

export const TariffSubStep1: React.FC<TariffSubStep1Props> = ( { formData, storeDevices, productData } ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const actions = useGarantieMaxActions();
  const { showAlert, hideAlert } = useAppAlertService();
  const logger = useAppLogger();
  const loadingOnFullContainer = useLoadingSpinnerOnFullContainer();
  const service = useWidgetService();
  const { insuredObjects, factors } = productData;

  const initialPageState = React.useMemo<TariffFormState>( () => {
    let dataTariff: TariffFormData = {
      tariffFormData: null,
      deviceItems: [],
    };

    if ( !storeDevices ) {
      const newItem: IDeviceItem = {
        ...emptyDevice,
        groupName: DeviceGroups.DeviceType,
      };

      const updatedDevices: IDeviceItem[] = [ newItem ];

      dataTariff.deviceItems = updatedDevices;
    } else {
      dataTariff.tariffFormData = formData;
      dataTariff.deviceItems = storeDevices;
    }
    return {
      isLoading: false,
      ...dataTariff,
    };
  }, [ formData, storeDevices ] );

  const [ tariffFormState, listAction ] = React.useReducer<TariffFormReducer>(
    tariffFormReducer, initialPageState,
  );

  const { isLoading, tariffFormData, deviceItems } = tariffFormState;

  const formContext = useForm( {
    mode: 'onChange',
    shouldUnregister: false,
  } );

  const { formState, control, errors, setValue, setError, clearErrors } = formContext;

  const onSubmit = React.useCallback( async ( tariffForm: ITariffDataStep ) => {
    if ( !formState.isValid ) {
      return;
    }

    const tariffData = prepareFormDataGM(
      deviceItems,
      productData.insuredObjects,
      tariffForm,
    );
    const sendData: ITariffDataSend = {
      formData: tariffData,
      deviceItems,
    };
    const result = await service.sendSubstep1Tariff( sendData );

    if ( result.errorCode === 0 ) {
      actions.goToSubStep( 'subStep2', true );
    } else {
      showAlert( {
        message: t( 'base:forms.messages.errorSave' ),
        type: 'danger',
      } );
      actions.goToTopPage();
    }
  }, [ actions, deviceItems, formState.isValid, productData.insuredObjects, service, showAlert, t ] );

  const updatedProductData = React.useCallback( (
    deviceTypeValue: number | string,
    objectItem: IBFInsuredObjectItem,
    insuredData: IBFInsuredObjectItem[],
  ) => {
    const idxInsuredObject = insuredData.findIndex( ( insured ) => insured.id === objectItem.id );

    if ( idxInsuredObject !== -1 ) {
      const productFields = insuredData[idxInsuredObject].productFields || objectItem.productFields;

      const idxField = productFields && productFields.findIndex(
        ( field ) => field.name === FieldDeviceTypes.DeviceType,
      );

      if ( idxField && idxField !== -1 && productFields ) {
        productFields[idxField].defaultValue = deviceTypeValue;
        setValue(
          `${FieldDeviceTypes.DeviceType}_${objectItem.id}`,
          deviceTypeValue,
          { shouldDirty: true },
        );
      }

      const updatedProduct: IProductDataGM = {
        ...productData,
        insuredObjects: insuredData,
      };

      actions.reload( updatedProduct );
    }
  }, [ actions, productData, setValue ] );

  const clearErrorsOnForm = React.useCallback ( ( ): void => {
    if ( errors ) {
      Object.keys( errors ).forEach( ( inputName ) => {
        clearErrors( inputName );
      } );
    }

  }, [ clearErrors, errors ] );

  const recoverDataForDevice = React.useCallback( (
    deviceTypeValue: number | string,
    insuredId: number,
    insuredObject: IBFInsuredObjectItem,
    deviceList: IDeviceItem[],
    currentInsuredId: number,
  ): ITariffDataStep | null => {
    const cloneTariffFormData = cloneDeep( tariffFormData );
    const variables = insuredObject.productFields;
    const newTariffData: ITariffDataStep = clearAddOnFamilyFields( cloneTariffFormData!, insuredObjects );

    variables?.forEach( ( variable ) => {
      const newNameField = `${variable.name}_${insuredObject.id}`;
      const oldNameField = `${variable.name}_${insuredId}`;

      const fieldValue = cloneTariffFormData && cloneTariffFormData[oldNameField];

      if ( fieldValue ) {
        newTariffData[newNameField] = variable.name === FieldDeviceTypes.DeviceType ? deviceTypeValue : fieldValue;
        if ( cloneTariffFormData && cloneTariffFormData[oldNameField] ) {
          delete cloneTariffFormData[oldNameField];
        }
      }
    } );

    const mobileDevice = deviceList.filter( ( device ) => device.typeDevice === DeviceTypes.MobileDevice );
    const defaultGroup = insuredObjects.find( ( group ) => group.name === InsuredObjectTypes.Default );

    if ( mobileDevice && mobileDevice.length >=2 ) {
      for ( let i = 0; i < mobileDevice.length; i++ ) {
        if ( i >= 1 ) {
          const device = mobileDevice[i];
          const familyItem = FamilyTypes[`Family${i}`];
          let familyVal = '1';

          if ( device.insuredId === currentInsuredId ) {
            familyVal = '';
          }

          if ( newTariffData ) {
            newTariffData[`${familyItem}_${defaultGroup?.id}`] = familyVal;
          }
        }
      }
    }

    return newTariffData;
  }, [ insuredObjects, tariffFormData ] );

  const onUpdatedDevices = React.useCallback( ( insuredId?: number ) => {
    const cloneDeviceItems = cloneDeep( deviceItems );
    let groupName = DeviceGroups.DeviceType;

    const filterDevice = cloneDeviceItems.filter( ( device ) => device.insuredId !== null );
    if ( filterDevice && filterDevice.length === maxCountDevices ) {
      return;
    }

    const filterMobileDevices = cloneDeviceItems.filter(
      ( device ) => device.typeDevice === DeviceTypes.MobileDevice,
    );

    if ( filterMobileDevices && filterMobileDevices.length >= maxCountMobile ) {
      groupName = DeviceGroups.DeviceTypeOther;
    }

    if ( insuredId ) {
      const idxDevice = cloneDeviceItems.findIndex( ( object ) => object.insuredId === insuredId );
      if ( idxDevice !== -1 ) {
        cloneDeviceItems[idxDevice].addedNewDevice = ActionDeviceTypes.Yes;
      }
    }

    const newDevice: IDeviceItem = {
      ...emptyDevice,
      groupName,
    };

    const updatedDevices: IDeviceItem[] = [ ...cloneDeviceItems, newDevice ];

    listAction( {
      type: 'UPDATE_DEVICES',
      payload: { data: updatedDevices },
    } );

  }, [ deviceItems ] );

  const onUpdatedDeviceType = React.useCallback( (
    idxDevice: number,
    deviceList: IDeviceItem[],
    deviceItem: IDeviceItem,
    factorItem: ICFFactorTypeOption,
    insuredData: IBFInsuredObjectItem[],
    fieldName: string,
  ) => {
    const foundFactor = getFactorItem( factorItem, factors );

    if ( foundFactor ) {
      const insuredObjectIds = deviceList.map( ( device ) => device.insuredId );
      const insuredObject = getInsuredObject( insuredData, foundFactor, deviceItem.insuredId );

      if ( insuredObject && insuredObject.deviceType === deviceItem.typeDevice ) {
        const updatedDevice: IDeviceItem = {
          ...deviceItem,
          value: factorItem,
        };

        updatedProductData( factorItem.key, insuredObject, insuredData );

        deviceList[idxDevice] = updatedDevice;

        listAction( {
          type: 'UPDATE_DEVICES',
          payload: { data: deviceList },
        } );
      } else {
        clearErrorsOnForm();
        const filterMobileDevices = deviceList.filter(
          ( device ) => device.typeDevice === DeviceTypes.MobileDevice,
        );

        if ( insuredObject && insuredObject.deviceType === DeviceTypes.MobileDevice
          && filterMobileDevices.length >= maxCountMobile ) {
          const device: IDeviceItem = {
            ...deviceItem,
          };

          deviceList[idxDevice] = device;

          listAction( {
            type: 'UPDATE_DEVICES',
            payload: { data: deviceList },
          } );

          setValue( fieldName, deviceItem.value );
          setError(
            fieldName,
            {
              shouldFocus: true,
              message: t( 'bookingFunnel.messagesOfLimitMobileGm' ),
            },
          );
          return;
        }

        if ( deviceList.length === maxCountDevices ) {
          const device: IDeviceItem = {
            ...deviceItem,
          };

          deviceList[idxDevice] = device;

          listAction( {
            type: 'UPDATE_DEVICES',
            payload: { data: deviceList },
          } );

          setValue( fieldName, deviceItem.value );
          setError(
            fieldName,
            {
              shouldFocus: true,
              message: t( 'bookingFunnel.messagesOfLimitGm', { countDevices: maxCountDevices } ),
            },
          );
          return;
        }

        const selectNewObject = selectInsuredObject( insuredData, foundFactor, insuredObjectIds );
        if ( selectNewObject !== null ) {
          if ( deviceItem ) {
            const newDevice: IDeviceItem = {
              ...deviceItem,
              value: factorItem,
              typeDevice: selectNewObject.deviceType,
              insuredId: selectNewObject.id,
              addOnFamily: false,
              showFields: true,
            };

            if ( selectNewObject.deviceType === DeviceTypes.MobileDevice && filterMobileDevices.length >= 1 ) {
              newDevice.addOnFamily = true;
              newDevice.showFields = true;
            }

            updatedProductData( foundFactor.key, selectNewObject, insuredData );

            deviceList[idxDevice] = newDevice;

            const newDeviceItems: IDeviceItem[] = updatedAddOnFamilyItems( deviceList );

            const payload: TariffFormData = {
              tariffFormData: recoverDataForDevice(
                foundFactor.key, deviceItem.insuredId!, selectNewObject, newDeviceItems, newDevice.insuredId!,
              ),
              deviceItems: newDeviceItems,
            };

            listAction( { type: 'SHOW_LOADING' } );

            // Needed small pause for rebuild data
            setTimeout( () => {
              listAction( {
                type: 'LOAD_SUCCESS',
                payload: { data: payload },
              } );
            }, 500 );
          }
        }
      }

      setValue( fieldName, factorItem );
    }
  }, [ clearErrorsOnForm, factors, recoverDataForDevice, setError, setValue, t, updatedProductData ] );

  const onDeviceChangeHandler = React.useCallback( async (
    item: ICFFactorTypeOption,
    insuredId: number | null,
    fieldName: string,
  ) => {
    hideAlert();

    const cloneInsuredObjects = cloneDeep( insuredObjects );
    const cloneDeviceItems = cloneDeep( deviceItems );
    const idxDevice = cloneDeviceItems.findIndex( ( object ) => object.insuredId === insuredId );

    let currentDevice: IDeviceItem | null = null;

    if ( idxDevice !== -1 ) {
      currentDevice = cloneDeviceItems[idxDevice];
    }

    if ( currentDevice && insuredId !== null ) {
      onUpdatedDeviceType( idxDevice, cloneDeviceItems, currentDevice, item, cloneInsuredObjects, fieldName );
      return;
    }

    const foundFactor = getFactorItem( item, factors );

    if ( foundFactor ) {
      const insuredObjectIds = cloneDeviceItems.map( ( device ) => device.insuredId );
      const selectNewObject = selectInsuredObject( cloneInsuredObjects, foundFactor, insuredObjectIds );

      if ( selectNewObject !== null ) {
        if ( currentDevice ) {
          const updatedDevice: IDeviceItem = {
            ...currentDevice,
            value: item,
            typeDevice: selectNewObject.deviceType,
            insuredId: selectNewObject.id,
            addOnFamily: false,
            showFields: true,
          };

          const filterMobileDevices = cloneDeviceItems.filter(
            ( device ) => device.typeDevice === DeviceTypes.MobileDevice,
          );

          if ( selectNewObject.deviceType === DeviceTypes.MobileDevice && filterMobileDevices.length >= 1 ) {
            updatedDevice.addOnFamily = true;
            updatedDevice.showFields = true;
          }

          updatedProductData( foundFactor.key, selectNewObject, cloneInsuredObjects );

          cloneDeviceItems[idxDevice] = updatedDevice;

          listAction( {
            type: 'UPDATE_DEVICES',
            payload: { data: cloneDeviceItems },
          } );

          setValue( fieldName, item, { shouldValidate: true } );
        }
      } else {
        showAlert( {
          message: t( 'base:forms.messages.error' ),
          type: 'danger',
        } );
        actions.goToTopPage();
      }
    }

  }, [ actions, deviceItems, factors, hideAlert, insuredObjects,
    onUpdatedDeviceType, setValue, showAlert, t, updatedProductData ] );

  const onAddDeviceHandler = React.useCallback( ( addedNewDevice: ActionDeviceTypes, insuredId: number ) => {
    if( addedNewDevice === ActionDeviceTypes.Yes ) {
      onUpdatedDevices( insuredId );
    }
  }, [ onUpdatedDevices ] );

  const isShowAddOnFamily = React.useCallback( ( ): boolean => {
    const cloneMobileDevices = deviceItems.filter(
      ( device ) => device.insuredId !== null && device.typeDevice === DeviceTypes.MobileDevice,
    );

    if ( cloneMobileDevices && cloneMobileDevices.length >= 2 ) {
      return true;
    }

    return false;
  }, [ deviceItems ] );

  const onShowFields = React.useCallback( ( isShowFields: boolean, insuredId: number ) => {
    if( insuredId ) {
      const cloneDeviceItems = cloneDeep( deviceItems );
      const idxDevice = cloneDeviceItems.findIndex( ( object ) => object.insuredId === insuredId );

      if ( idxDevice !== -1 ) {
        cloneDeviceItems[idxDevice].showFields = isShowFields;

        listAction( {
          type: 'UPDATE_DEVICES',
          payload: { data: cloneDeviceItems },
        } );
      }
    }
  }, [ deviceItems ] );

  const downloadStaticPdfHandler = React.useCallback(
    async ( e: React.MouseEvent<HTMLAnchorElement>,
    ): Promise<void> => {
      e.preventDefault();
      hideAlert();

      const document = productData.staticDocuments.find( ( d ) => d.entityType === 'static_ERST' );

      if( !document ) {
        showAlert( {
          message: t( 'base:forms.messages.error' ),
          type: 'danger',
        } );

        return;
      }

      listAction( { type: 'SHOW_LOADING' } );

      return service.downloadStaticPdfDocument( document.code )
        .then( ( res ) => {
          const blob = new Blob( [ res ], {
            type: 'application/pdf',
          } );
          FileSaver.saveAs( blob, 'Erstinformationen.pdf' );
          listAction( { type: 'HIDE_LOADING' } );
        } )
        .catch( ( error ) => {
          logger.error( error );
          showAlert( {
            message: t( 'base:forms.messages.error' ),
            type: 'danger',
          } );
          listAction( { type: 'HIDE_LOADING' } );
        } );
    }, [ hideAlert, logger, productData.staticDocuments, service, showAlert, t ] );

  if ( productData === null ) {
    return <LoadingSpinner />;
  }

  return (
    <FormProvider { ...formContext }>
      <Row className="max-w-450 mt-3" id="tariff-sub-step1">
        { isLoading && loadingOnFullContainer }
        <Col md={ 12 } className="head-tab text-center px-0">
          <h1 className="f-26 text-c-black">{ t( 'bookingFunnel.tariff.subStep1Title' ) }</h1>
        </Col>
        <Col md={ 12 } className="head-tab mt-4 mb-5 px-0 text-center">
          <h2 className="f-18 text-c-black">{ t( 'bookingFunnel.tariff.subStep1SubTitleGM' ) }</h2>
        </Col>
        <Col md={ 12 } className="px-0">
          <Form noValidate onSubmit={ formContext.handleSubmit( onSubmit ) }>
            <Row>
              { deviceItems.length > 0 && deviceItems.map( ( device, idx ) => (
                <Col key={ idx } sm={ 12 } className="mb-4">
                  <h3 className="my-3 f-16 font-weight-normal">
                    <img className="mr-2 mb-1" src={ deviceIconUrl } alt={ `Device Icon ${idx}` } />
                    { t( 'bookingFunnel.tariff.deviceTitle', { idx: idx + 1 } ) }
                  </h3>
                  <Controller
                    name={ `${device.groupName}_${idx}` }
                    control={ control }
                    rules={ { required: true } }
                    defaultValue={ device.value || '' }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <Form.Label>{ t( 'bookingFunnel.tariff.deviceLabel' ) }</Form.Label>
                        <SelectDevicePanel
                          insuredId={ device.insuredId }
                          selectValue={ device.value! }
                          nameField={ props.name }
                          listValues={ factors.find( ( f ) => f.group === device.groupName! )?.values! }
                          onChangeHandler={ onDeviceChangeHandler }
                        />
                        <Form.Control
                          type="hidden"
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { errors[props.name]?.message ? (
                            errors[props.name]?.message
                          ) : (
                            <Fragment>
                              { t( 'base:forms.messages.fieldRequired',
                                { fieldLabel: t( 'bookingFunnel.tariff.deviceLabel' ) } ) }
                            </Fragment>
                          ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                  { !isLoading && device && device.addOnFamily && isShowAddOnFamily() && (
                    <AddOnFamilyPanel
                      nameField={ `${device.groupName}_${idx}` }
                      insuredObjects={ insuredObjects }
                      deviceItems={ deviceItems.filter( ( item ) => item.typeDevice === DeviceTypes.MobileDevice ) }
                      formData={ tariffFormData! }
                      factors={ factors }
                      insuredId={ device.insuredId! }
                      onChangeHandler={ onShowFields }
                    />
                  ) }
                  { device.showFields && device.insuredId !== null
                    && insuredObjects.filter( ( insured ) => insured.id === device.insuredId )
                      .map( ( insured, index ) => (
                        <Fragment key={ index }>
                          { insured.productFields && insured.productFields
                            .map( ( variable, itemIdx ) => (
                              <Fragment key={ itemIdx }>
                                <FieldPanal
                                  formData={ tariffFormData! }
                                  factors={ factors }
                                  variable={ variable }
                                />
                              </Fragment>
                            ) ) }
                          { deviceItems.length < maxCountDevices && (
                            <AddDeviceElement
                              valField={ device.addedNewDevice }
                              insuredId={ device.insuredId! }
                              onChangeHandler={ onAddDeviceHandler }
                            />
                          ) }
                        </Fragment>
                      ) ) }
                </Col>
              ) ) }
              { deviceItems && (
                <Col sm={ 12 } className="mb-4">
                  <Controller
                    name="isAcceptedBrokerInfo"
                    control={ control }
                    rules={ { required: true } }
                    defaultValue={
                      tariffFormData && tariffFormData.isAcceptedBrokerInfo ? tariffFormData.isAcceptedBrokerInfo : ''
                    }
                    render={ ( props ) => (
                      <FormGroup controlId={ props.name }>
                        <div className="gm-checkbox-box">
                          <FormControl
                            { ...props }
                            className="form-check-input m-0 wd-24"
                            type="checkbox"
                            isInvalid={ errors?.[props.name] !== undefined }
                            onChange={ ( e: React.ChangeEvent<HTMLInputElement> ) =>
                              props.onChange( e.target.checked )
                            }
                            checked={ props.value }
                            onBlur={ props.onBlur }
                          />
                          <Form.Label className="f-16 checkbox-label">
                            <Trans
                              i18nKey="bookingFunnel.personalData.acceptedBrokerInfoText"
                              ns="widgets"
                              components={ {
                                linkToFile:
                                  <a href={ blankLink } onClick={ downloadStaticPdfHandler }>
                                    Link
                                  </a>,
                              } }
                            />
                          </Form.Label>
                        </div>
                        <Form.Control
                          type="hidden"
                          isInvalid={ errors[props.name] !== undefined }
                        />
                        <Form.Control.Feedback type="invalid">
                          { t( 'bookingFunnel.summary.checkBoxErr' ) }
                        </Form.Control.Feedback>
                      </FormGroup>
                    ) }
                  />
                </Col>
              ) }
            </Row>
            <Row className="m-0 mt-5">
              <Col sm={ 12 } className="d-flex justify-content-center m-0 p-0">
                <Button
                  id={ `${GMBFStep.TariffSubStep1}_next` }
                  type="submit"
                  variant="primary"
                  disabled={ !formState.isValid || formState.isSubmitting }
                  className="mr-0"
                >
                  { t( 'bookingFunnel.nextBtn' ) }
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </FormProvider>
  );
};
